import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuizSettings, QuizResults } from '../types/quiz';
import useQuiz from '../hooks/useQuiz';
import {
  Container,
  Card,
  QuestionText,
  AnswerButton,
  CategoryTag,
  DifficultyTag,
  ScoreText,
  LivesContainer,
  Heart,
  LoadingSpinner,
  ErrorText,
  ProgressBar,
  ProgressFill,
  ButtonGroup,
  Button
} from '../styles/StyledComponents';

interface QuizProps {
  settings: QuizSettings;
  onGameOver: (results: QuizResults) => void;
}

const QuizComponent: React.FC<QuizProps> = ({ settings, onGameOver }) => {
  const navigate = useNavigate();
  const gameOverHandledRef = useRef(false);
  
  const { 
    state, 
    selectedAnswer, 
    answerChecked, 
    handleAnswerClick, 
    skipQuestion 
  } = useQuiz(settings);
  
  // Check for game over condition
  React.useEffect(() => {
    // Only handle game over once to prevent infinite loops
    if (state.gameOver && !gameOverHandledRef.current) {
      gameOverHandledRef.current = true;
      onGameOver({ score: state.score, totalQuestions: state.questions.length });
      // Use setTimeout to break the synchronous update cycle
      setTimeout(() => {
        navigate('/game-over');
      }, 0);
    }
  }, [state.gameOver, state.score, state.questions.length, onGameOver, navigate]);

  if (state.loading) {
    return (
      <Container>
        <LoadingSpinner />
        <p>Loading questions...</p>
      </Container>
    );
  }

  if (state.error) {
    return (
      <Container>
        <Card>
          <ErrorText>{state.error}</ErrorText>
          <Button onClick={() => navigate('/')}>Back to Settings</Button>
        </Card>
      </Container>
    );
  }

  if (state.questions.length === 0) {
    return (
      <Container>
        <Card>
          <ErrorText>No questions available. Please try different settings.</ErrorText>
          <Button onClick={() => navigate('/')}>Back to Settings</Button>
        </Card>
      </Container>
    );
  }

  const currentQuestion = state.questions[state.currentQuestionIndex];
  const progress = (state.currentQuestionIndex / state.questions.length) * 100;

  return (
    <Container>
      <Card>
        <ProgressBar>
          <ProgressFill progress={progress} />
        </ProgressBar>
        
        <ScoreText>Score: {state.score}</ScoreText>
        
        <LivesContainer>
          {[...Array(settings.lives)].map((_, i) => (
            <Heart key={i} data-filled={i < state.lives ? 'true' : 'false'}>❤</Heart>
          ))}
        </LivesContainer>
        
        <CategoryTag>{currentQuestion.category}</CategoryTag>
        <DifficultyTag difficulty={currentQuestion.difficulty}>
          {currentQuestion.difficulty.charAt(0).toUpperCase() + currentQuestion.difficulty.slice(1)}
        </DifficultyTag>
        
        <QuestionText>{currentQuestion.question}</QuestionText>
        
        {currentQuestion.all_answers.map((answer, index) => {
          let answerState = 'default';
          if (answerChecked && answer === currentQuestion.correct_answer) {
            answerState = 'correct';
          } else if (answerChecked && selectedAnswer === answer && answer !== currentQuestion.correct_answer) {
            answerState = 'incorrect';
          } else if (selectedAnswer === answer && !answerChecked) {
            answerState = 'selected';
          }
          
          return (
            <AnswerButton
              key={index}
              onClick={() => handleAnswerClick(answer)}
              disabled={answerChecked}
              data-answer-state={answerState}
            >
              {answer}
            </AnswerButton>
          );
        })}
        
        <ButtonGroup>
          <Button onClick={() => navigate('/')} disabled={answerChecked}>
            Quit
          </Button>
          <Button 
            onClick={skipQuestion}
            disabled={answerChecked || state.skipsRemaining <= 0}
          >
            Skip ({state.skipsRemaining})
          </Button>
        </ButtonGroup>
      </Card>
    </Container>
  );
};

export default QuizComponent;
